import { Component } from '@theme/component';

/**
 * @typedef {Object} CountdownTimerRefs
 * @property {HTMLSpanElement} days - The days display element.
 * @property {HTMLSpanElement} hours - The hours display element.
 * @property {HTMLSpanElement} minutes - The minutes display element.
 * @property {HTMLSpanElement} seconds - The seconds display element.
 */

/**
 * Countdown timer component that counts down to a target date.
 * Hides itself when the countdown expires.
 *
 * @extends {Component<CountdownTimerRefs>}
 */
class CountdownTimer extends Component {
  requiredRefs = ['days', 'hours', 'minutes', 'seconds'];

  #intervalId = null;

  connectedCallback() {
    super.connectedCallback();
    this.#startCountdown();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopCountdown();
  }

  /** @returns {number | null} */
  get #targetTime() {
    const date = this.dataset.date;
    const time = this.dataset.time || '00:00';
    if (!date) return null;
    return new Date(`${date}T${time}`).getTime();
  }

  #startCountdown() {
    this.#update();
    this.#intervalId = setInterval(() => this.#update(), 1000);
  }

  #stopCountdown() {
    if (this.#intervalId) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  #update() {
    const target = this.#targetTime;
    if (!target) return;

    const now = Date.now();
    const diff = target - now;

    if (diff <= 0) {
      this.#stopCountdown();
      this.hidden = true;
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    this.refs.days.textContent = String(days).padStart(2, '0');
    this.refs.hours.textContent = String(hours).padStart(2, '0');
    this.refs.minutes.textContent = String(minutes).padStart(2, '0');
    this.refs.seconds.textContent = String(seconds).padStart(2, '0');
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
